/**
 * "Only this run / This and following runs" — the choice Google Calendar
 * puts in front of every edit to a recurring event.
 *
 * THIS AND FOLLOWING, NOT ALL (2026-10-30). The move used to offer "All
 * events in this series", which rewrote the rule for the whole series,
 * PAST INCLUDED: recorded outcomes fell off the calendar, skipped dates
 * came back and moved runs snapped home -- while its help line said it
 * moved "every future date". It is now what the words say: this run and
 * every later one, as a timing edit the api-server applies from this run
 * on (0.6.54), with the past left exactly as it was. `seriesNote` says
 * what that will do, from the server's own dry run -- including the
 * runs before it, which keep their old time on a second schedule. On a
 * row with an end it reaches that end only (`seriesStop`), and a later
 * row of its plan keeps its own time: the note names it, and the
 * button's words stop where the move does.
 *
 * AND THE OTHER ROWS ON DELETE. An edit that kept the old time lists
 * the schedule more than once under the same name: the old time until
 * the change day, the new time after it (`keptOldTime`,
 * `newTimeAfter`), one row per change. Pausing or deleting one leaves
 * the others running, so a delete names every row and what it still has
 * to run (`plan`), and Pause or Delete then asks, as Pause does
 * everywhere else (`KeptPauseConfirm`): all of them, or only this one.
 * Neither is assumed: the operator chose this schedule, and the others
 * are theirs to add. A delete also says what it takes off the calendar:
 * the record of the schedule's missed runs goes with it.
 *
 * It is a THREE-way decision (one, series, cancel), so it cannot reuse
 * `ConfirmDialog`, whose whole shape is confirm-or-cancel. Defaulting to
 * either branch silently is the one thing this dialog exists to prevent:
 * dragging one clean to 15:00 and moving every future clean with it is
 * indistinguishable, at the moment of the drag, from the intended edit.
 *
 * Portalled to `document.body` with `data-nx-modal-layer` for the same
 * reason `ConfirmDialog` is — see the note there about drawers using
 * `useDismissOnOutside` unmounting their own dialogs otherwise.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { KeptPauseConfirm } from './calendar/KeptPauseConfirm';
import './OccurrenceScopeDialog.css';

/** Said beside the choices of a delete, where "Skip this run" is one of
 *  them -- not on the "Delete them too?" question that follows, where
 *  skipping is not a choice. */
export const SKIP_ONLY_NOTE = 'Skipping affects only this date.';

/** What a delete says it takes off the calendar besides future runs. */
export const DELETE_MISSED_NOTE =
  'Missed runs of a deleted schedule leave the calendar; finished cleans stay in the task list.';

/**
 * `pause` is offered on DELETE only, and deliberately between the other
 * two. Deleting a series is the one irreversible verb on this screen —
 * it drops the rule and its `schedule_fires` ledger with it — and it was
 * until now the ONLY way to stop a schedule. "Stop the 06:00 clean while
 * the west wing is being renovated" is not a delete, but a delete is
 * what an operator with no other button reaches for.
 */
export type OccurrenceScope = 'one' | 'series' | 'pause';
export type OccurrenceScopeMode = 'move' | 'delete';

export function OccurrenceScopeDialog({
  open,
  mode,
  single,
  title,
  detail,
  seriesNote,
  seriesStop,
  seriesChecking,
  seriesError,
  plan,
  pending,
  error,
  onChoose,
  onCancel,
}: {
  open: boolean;
  mode: OccurrenceScopeMode;
  /**
   * The schedule has exactly one occurrence, so "this event" and "the
   * whole series" name the same task. Asking anyway is a question with
   * two identical answers, and one of them ("skip this event") would
   * leave behind a schedule that can never fire again and shows up
   * nowhere — so the dialog collapses to a plain confirm and takes the
   * branch that removes it properly.
   */
  single?: boolean;
  /** Usually the schedule's name. */
  title: string;
  /** What the choice will do, in the user's terms. */
  detail?: string;
  /** What "This and following runs" will do (a move only): when the new
   *  time takes over, what it gives up, and what it keeps. */
  seriesNote?: string;
  /** Where "This and following runs" stops, for a row with an end
   *  ("until Tue 03/Nov"): its button then says the move stops there,
   *  not that it moves "every later one". */
  seriesStop?: string;
  /** That note is still being worked out (the server's dry run has not
   *  answered): said in its place, and "This and following runs" waits
   *  for it, so the choice is never made without it. */
  seriesChecking?: boolean;
  /** The dry run failed: said in its place, and "This and following
   *  runs" stays off. "Only this run" and Cancel stay on throughout. */
  seriesError?: string | null;
  /** A delete only: the schedule is listed more than once after a
   *  change of time. `intro` and `rows` name every row still to run and
   *  what it has left, this one first; `count` is how many OTHER rows
   *  "Delete all" takes along, `pauseCount` how many of them are still
   *  running for "Pause all" (none: Pause asks nothing more). */
  plan?: {
    intro: string;
    rows: readonly string[];
    count: number;
    pauseCount?: number;
  };
  pending?: boolean;
  error?: string | null;
  /** `wholePlan`: Pause or Delete takes every row of `plan` along. */
  onChoose: (scope: OccurrenceScope, wholePlan?: boolean) => void;
  onCancel: () => void;
}) {
  // Pause or Delete with other rows listed: the question it asks first.
  const [asking, setAsking] = useState<'pause' | 'series' | null>(null);
  const [openSeen, setOpenSeen] = useState(open);
  if (openSeen !== open) {
    setOpenSeen(open);
    setAsking(null);
  }

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  const planned = !single && mode === 'delete' && plan !== undefined && plan.count > 0;
  const pauseCount = plan?.pauseCount ?? plan?.count ?? 0;
  const asks = (scope: 'pause' | 'series') => planned && (scope === 'series' || pauseCount > 0);
  const choose = (scope: 'pause' | 'series') => (asks(scope) ? setAsking(scope) : onChoose(scope));

  // "Skip this run" is on screen: a delete's choices, not its question.
  const skipOffered = mode === 'delete' && !single && !(asking !== null && planned);
  // One word for one thing: a "run" of a "schedule", in every choice.
  const oneLabel = mode === 'delete' ? 'Skip this run' : 'Only this run';
  const seriesLabel = mode === 'delete' ? 'Delete the schedule' : 'This and following runs';

  return createPortal(
    <div
      className="nx-occ-scope__backdrop"
      data-nx-modal-layer=""
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="nx-occ-scope"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="nx-occ-scope-title"
        data-testid="occurrence-scope-dialog"
      >
        <h2 className="nx-occ-scope__title" id="nx-occ-scope-title">
          {single
            ? mode === 'delete'
              ? 'Delete scheduled task'
              : 'Move scheduled task'
            : mode === 'delete'
              ? 'Delete recurring task'
              : 'Move recurring task'}
        </h2>
        <p className="nx-occ-scope__name">{title}</p>
        {(detail || skipOffered) && (
          <p className="nx-occ-scope__detail" data-testid="occurrence-scope-detail">
            {[detail, skipOffered ? SKIP_ONLY_NOTE : null].filter(Boolean).join(' ')}
          </p>
        )}
        {!single && mode === 'move' && seriesError ? (
          <p
            className="nx-occ-scope__error"
            role="alert"
            data-testid="occurrence-scope-series-error"
          >
            Couldn&apos;t check what &quot;This and following runs&quot; changes: {seriesError}
          </p>
        ) : !single && mode === 'move' && seriesChecking ? (
          <p
            className="nx-occ-scope__detail nx-occ-scope__series-note"
            data-testid="occurrence-scope-series-checking"
          >
            Checking what this changes…
          </p>
        ) : (
          !single &&
          mode === 'move' &&
          seriesNote && (
            <p
              className="nx-occ-scope__detail nx-occ-scope__series-note"
              data-testid="occurrence-scope-series-note"
            >
              {seriesNote}
            </p>
          )
        )}
        {planned && (
          <div className="nx-occ-scope__kept" data-testid="occurrence-scope-kept">
            <p className="nx-occ-scope__kept-text">{plan!.intro}</p>
            <ul className="nx-occ-scope__kept-rows">
              {plan!.rows.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        )}
        {!single && mode === 'delete' && (
          <p className="nx-occ-scope__detail" data-testid="occurrence-scope-delete-note">
            {DELETE_MISSED_NOTE}
          </p>
        )}
        {asking !== null && planned ? (
          <KeptPauseConfirm
            text=""
            count={asking === 'pause' ? pauseCount : plan!.count}
            verb={asking === 'pause' ? 'pause' : 'delete'}
            disabled={pending}
            onBoth={() => onChoose(asking, true)}
            onOne={() => onChoose(asking, false)}
            onCancel={() => setAsking(null)}
            className="nx-occ-scope__ask"
            noteClass="nx-occ-scope__ask-text"
            actionsClass="nx-occ-scope__actions"
            btnClass="nx-occ-scope__btn"
            bothClass={`nx-occ-scope__btn nx-occ-scope__btn--${asking === 'pause' ? 'pause' : 'danger'}`}
            testId="occurrence-scope-plan"
          />
        ) : (
          <div className="nx-occ-scope__actions">
            {single ? (
              // `series` on a one-occurrence schedule: a move rewrites its
              // start, a delete removes the whole entry. Both are what
              // "this task" means when there is only one.
              <button
                type="button"
                className={`nx-occ-scope__btn nx-occ-scope__btn--${mode === 'delete' ? 'series' : 'one'}`}
                disabled={pending}
                onClick={() => onChoose('series')}
                data-testid="occurrence-scope-single"
              >
                {mode === 'delete' ? 'Delete it' : 'Move it'}
              </button>
            ) : (
              <button
                type="button"
                className="nx-occ-scope__btn nx-occ-scope__btn--one"
                disabled={pending}
                onClick={() => onChoose('one')}
                data-testid="occurrence-scope-one"
              >
                {oneLabel}
              </button>
            )}
            {!single && mode === 'delete' && (
              <button
                type="button"
                className="nx-occ-scope__btn nx-occ-scope__btn--pause"
                disabled={pending}
                title="Stops every future run. Its times and history are kept, and resuming brings it all back."
                onClick={() => choose('pause')}
                data-testid="occurrence-scope-pause"
              >
                Pause the schedule
              </button>
            )}
            {!single && (
              <button
                type="button"
                className="nx-occ-scope__btn nx-occ-scope__btn--series"
                disabled={
                  pending || (mode === 'move' && (Boolean(seriesChecking) || Boolean(seriesError)))
                }
                title={
                  mode !== 'move'
                    ? undefined
                    : seriesStop
                      ? `Moves this run and every later one ${seriesStop} to the new time. Runs after that, earlier runs, and runs that already happened stay as they were.`
                      : 'Moves this run and every later one to the new time. Earlier runs, and runs that already happened, stay as they were.'
                }
                onClick={() => (mode === 'delete' ? choose('series') : onChoose('series'))}
                data-testid="occurrence-scope-series"
              >
                {seriesLabel}
              </button>
            )}
            <button
              type="button"
              className="nx-occ-scope__btn nx-occ-scope__btn--cancel"
              disabled={pending}
              onClick={onCancel}
              data-testid="occurrence-scope-cancel"
            >
              Cancel
            </button>
          </div>
        )}
        {error && (
          <p className="nx-occ-scope__error" role="alert" data-testid="occurrence-scope-error">
            {error}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
